/**
 * Delivery Detail Page
 * Displays detailed information about a specific delivery
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import type { Delivery } from '@/modules/commerce/delivery/types/delivery';
import type { OrderDetails } from '@/types/order';
import { getDeliveryById } from '@/services/commerce/deliveryService';
import { StatusBadge } from '@/modules/commerce/delivery/components/StatusBadge';
import { ApprovalSection } from '@/components/commerce/order/ApprovalSection';
import { orderService } from '@/services/commerce/orderService';
import { toast } from 'sonner';

export default function DeliveryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [delivery, setDelivery] = useState<Delivery | null>(
    (location.state as { delivery?: Delivery })?.delivery || null
  );
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(!delivery);
  const [orderLoading, setOrderLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!delivery && id) {
      fetchDelivery(parseInt(id, 10));
    }
  }, [id, delivery]);

  useEffect(() => {
    if (delivery?.salesOrderId) {
      fetchOrder(delivery.salesOrderId);
    }
  }, [delivery?.salesOrderId]);

  const fetchDelivery = async (deliveryId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getDeliveryById(deliveryId);
      setDelivery(data);
    } catch (err: any) {
      console.error('Failed to fetch delivery:', err);
      setError(err.message || 'Failed to load delivery details');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrder = async (orderId: number) => {
    try {
      setOrderLoading(true);
      const data = await orderService.getOrderById(orderId);
      setOrder(data);
    } catch (err: any) {
      console.error('Failed to fetch order:', err);
      // Non-critical: don't show error if order fetch fails
      setOrder(null);
    } finally {
      setOrderLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/sales/delivery');
  };

  const handleEdit = () => {
    if (delivery) {
      navigate(`/sales/delivery/${delivery.id}/edit`, { state: { delivery } });
    }
  };

  const handleApprovalSuccess = () => {
    // Refresh delivery data after approval
    if (delivery?.id) {
      fetchDelivery(delivery.id);
    }
    toast.success('Delivery approved successfully!');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">Loading delivery details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p className="font-bold">Error</p>
            <p>{error}</p>
            <button
              onClick={handleBack}
              className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Back to Deliveries
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!delivery) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
            <p>Delivery not found</p>
            <button
              onClick={handleBack}
              className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded hover:bg-yellow-700"
            >
              Back to Deliveries
            </button>
          </div>
        </div>
      </div>
    );
  }

  const canEdit = delivery.status !== 'DELIVERED' && delivery.status !== 'CANCELLED';

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex justify-between items-start">
          <div>
            <button
              onClick={handleBack}
              className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Deliveries
            </button>
            <h1 className="text-3xl font-bold text-gray-900">
              Delivery {delivery.deliveryNumber || delivery.externalId || `#${delivery.id}`}
            </h1>
            <p className="text-gray-600 mt-1">View delivery details and tracking information</p>
          </div>
          {canEdit && (
            <button
              onClick={handleEdit}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Delivery
            </button>
          )}
        </div>

        {/* Approval Section - Show when delivery is DELIVERED and order is awaiting approval */}
        {delivery.status === 'DELIVERED' && order && !orderLoading && (
          <div className="mb-6">
            <ApprovalSection
              order={order}
              onApprovalSuccess={handleApprovalSuccess}
            />
          </div>
        )}

        {/* Main Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Delivery Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Delivery Information</h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Delivery ID</span>
                <span className="font-medium">{delivery.deliveryNumber || delivery.externalId || `DEL-${delivery.id}`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sales Order ID</span>
                <span className="font-medium">{delivery.salesOrderId}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Status</span>
                <StatusBadge status={delivery.status} />
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Assigned From</span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  delivery.assignedFrom === 'UrjaVyapar'
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-orange-100 text-orange-800'
                }`}>
                  {delivery.assignedFrom || 'Unknown'}
                </span>
              </div>
              {delivery.scheduledDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Scheduled Date</span>
                  <span className="font-medium">{new Date(delivery.scheduledDate).toLocaleDateString()}</span>
                </div>
              )}
              {delivery.actualDeliveryDate && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Actual Delivery Date</span>
                  <span className="font-medium">{new Date(delivery.actualDeliveryDate).toLocaleDateString()}</span>
                </div>
              )}
            </div>
          </div>

          {/* Driver Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Driver Information</h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Driver Name</span>
                <span className="font-medium">{delivery.driverName || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Driver Phone</span>
                <span className="font-medium">{delivery.driverPhone || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Vehicle Number</span>
                <span className="font-medium">{delivery.vehicleNumber || '-'}</span>
              </div>
              {delivery.currentLocation && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Location</span>
                  <span className="font-medium">{delivery.currentLocation}</span>
                </div>
              )}
            </div>
          </div>

          {/* Address & Contact */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Address & Contact</h2>
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Customer Name</span>
                <span className="font-medium">{delivery.customerName || '-'}</span>
              </div>
              <div>
                <span className="text-gray-600 block mb-1">Delivery Address</span>
                <span className="font-medium">{delivery.deliveryAddress || delivery.dropAddress || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Contact Person</span>
                <span className="font-medium">{delivery.dropContactName || delivery.contactPerson || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Contact Phone</span>
                <span className="font-medium">{delivery.dropContactPhone || delivery.contactPhone || '-'}</span>
              </div>
            </div>
          </div>

          {/* Additional Details */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2">Additional Details</h2>
            <div className="space-y-4">
              {delivery.deliveredQuantity && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Delivered Quantity</span>
                  <span className="font-medium">{delivery.deliveredQuantity}</span>
                </div>
              )}
              {delivery.statusReason && (
                <div>
                  <span className="text-gray-600 block mb-1">Status Reason</span>
                  <span className="font-medium">{delivery.statusReason}</span>
                </div>
              )}
              {delivery.notes && (
                <div>
                  <span className="text-gray-600 block mb-1">Notes</span>
                  <span className="font-medium">{delivery.notes}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-600">Created At</span>
                <span className="font-medium">
                  {delivery.createdAt ? new Date(delivery.createdAt).toLocaleString() : '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Updated At</span>
                <span className="font-medium">
                  {delivery.updatedAt ? new Date(delivery.updatedAt).toLocaleString() : '-'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
